/**
 * @file Turns one goal read and the figures beside it into what a goal cell
 * renders: loading, no goal, denied, failed, or the `goalProgress` result.
 * Consumer: `useAnalyticsGoalProgress`, for the Overview and Home, so the Goal
 * tile and any table row computed from the same read always agree. A goal that is not
 * found, has a zero target or covers no day of the period is "no goal", never
 * zero; a target that is not a decimal fails with a message instead of
 * throwing in render.
 */
import { analyticsDecimalFraction } from '#types';
import { goalProgress } from './goalProgress';
import type {
  AnalyticsGoalRead,
  AnalyticsGoalReadProgress,
  GoalProgressInput,
} from './analyticsOverviewTab.types';

export const analyticsGoalReadProgress = (
  read: AnalyticsGoalRead,
  input: Omit<GoalProgressInput, 'goal'>,
): AnalyticsGoalReadProgress => {
  if (read.status === 'notFound') return { status: 'noGoal' };
  if (read.status !== 'found') return read;
  if (analyticsDecimalFraction(read.goal.target) === undefined)
    return { status: 'failed', message: 'The goal holds no decimal target.' };
  const progress = goalProgress({ ...input, goal: read.goal });
  return progress === null ? { status: 'noGoal' } : { status: 'set', progress };
};
